/* ══════════════════════════════════════════════════════════════════════
   HeroConsole — the signature, exclusively-designed product visual.

   A fully coded (SVG + CSS) mock of the SEOintent dashboard. Replaces the
   old AI-generated raster PNG. Everything here is vector-crisp on retina,
   uses the exact brand palette (#FF2F2F / #002FA7), and animates in with
   pure CSS keyframes (no Framer Motion — keeps the hero bundle lean).

   Structure:
     ┌─ window chrome (dots + url pill) ───────────────────────────┐
     │ rail │  AI Answer Visibility (big)   │  Keyword clusters     │
     │      │  ───────────────────────────  │  ───────────────────  │
     │      │  engine rows + sparklines      │  RankMath score ring │
     └──────┴────────────────────────────────┴──────────────────────┘
        + two floating accent cards for depth
   ════════════════════════════════════════════════════════════════════ */

const RED = "#FF2F2F";
const BLUE = "#002FA7";

/* ---- tiny inline sparkline ------------------------------------------- */
function Sparkline({ points, stroke = RED, w = 72, h = 22 }) {
  const max = Math.max(...points);
  const min = Math.min(...points);
  const span = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - ((p - min) / span) * h).toFixed(1)}`)
    .join(" ");
  const id = `spk-${stroke.replace("#", "")}-${points.join("")}`.slice(0, 24);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="overflow-visible" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.28" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${d} L ${w} ${h} L 0 ${h} Z`} fill={`url(#${id})`} />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---- circular score ring --------------------------------------------- */
function ScoreRing({ value = 94, size = 62 }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#ECECEF" strokeWidth="6" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={RED} strokeWidth="6"
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ animation: "ring-draw 1.4s cubic-bezier(.4,0,.2,1) both" }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="font-mono-data" fontSize="17" fontWeight="700" fill="#18181b">{value}</text>
    </svg>
  );
}

/* ---- engine glyph (small colored mark, no trademarks) ---------------- */
function EngineMark({ bg, glyph }) {
  return (
    <span
      className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-md text-[11px] font-bold text-white"
      style={{ background: bg }}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
}

const ENGINES = [
  { name: "ChatGPT",    bg: "#0F9D80", glyph: "◍", count: "1,204", delta: "+22%", pts: [4,5,6,6,8,9,12,14], up: true },
  { name: "Perplexity", bg: "#1FA5A5", glyph: "≈", count: "842",   delta: "+17%", pts: [3,4,4,6,7,7,9,11],  up: true },
  { name: "Gemini",     bg: BLUE,      glyph: "✦", count: "731",   delta: "+31%", pts: [2,3,3,5,6,8,10,13], up: true },
  { name: "Claude",     bg: "#C6613F", glyph: "✳", count: "704",   delta: "+14%", pts: [5,5,6,7,7,8,9,10],  up: true },
];

/* ---- keyword cluster mini-map ---------------------------------------- */
function ClusterMap() {
  const nodes = [
    { x: 44, y: 20, label: "beans" },
    { x: 150, y: 16, label: "organic" },
    { x: 158, y: 66, label: "roast" },
    { x: 40, y: 74, label: "brewing" },
    { x: 150, y: 108, label: "fair-trade" },
    { x: 42, y: 116, label: "grinder" },
  ];
  const cx = 96, cy = 66;
  return (
    <svg viewBox="0 0 200 136" className="w-full h-auto" aria-hidden="true">
      {nodes.map((n, i) => (
        <line key={i} x1={cx} y1={cy} x2={n.x} y2={n.y} stroke={RED} strokeOpacity="0.28" strokeWidth="1.25" />
      ))}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="3.2" fill="#fff" stroke={BLUE} strokeWidth="1.4" />
          <rect x={n.x - 26} y={n.y - 18} width="52" height="13" rx="6.5" fill="#F4F4F6" />
          <text x={n.x} y={n.y - 9} textAnchor="middle" fontSize="7.5" fill="#52525b" className="font-mono-data">{n.label}</text>
        </g>
      ))}
      {/* core node */}
      <circle cx={cx} cy={cy} r="24" fill={RED} fillOpacity="0.08" />
      <circle cx={cx} cy={cy} r="15" fill={RED} />
      <text x={cx} y={cy - 1} textAnchor="middle" fontSize="7" fontWeight="700" fill="#fff">coffee</text>
      <text x={cx} y={cy + 7} textAnchor="middle" fontSize="5.5" fill="#fff" fillOpacity="0.85">pillar</text>
    </svg>
  );
}

/* ---- sidebar rail icon ----------------------------------------------- */
function RailIcon({ d, active }) {
  return (
    <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${active ? "bg-[#FF2F2F]/10" : ""}`}>
      <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none"
        stroke={active ? RED : "#a1a1aa"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {d}
      </svg>
    </div>
  );
}

export default function HeroConsole() {
  return (
    <div className="relative mx-auto w-full max-w-5xl [perspective:1600px]">
      {/* ambient glow behind the window */}
      <div aria-hidden="true" className="pointer-events-none absolute -inset-x-10 -top-10 -bottom-16 -z-10">
        <div className="absolute left-1/2 top-1/2 h-[70%] w-[80%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF2F2F]/10 blur-[90px]" />
        <div className="absolute right-[8%] top-[10%] h-[40%] w-[40%] rounded-full bg-[#002FA7]/10 blur-[80px]" />
      </div>

      {/* the app window */}
      <div className="animate-console-in overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-[0_30px_80px_-20px_rgba(16,16,20,0.28)] ring-1 ring-black/[0.02]">
        {/* chrome bar */}
        <div className="flex h-11 items-center gap-2 border-b border-zinc-100 bg-zinc-50/80 px-4">
          <div className="flex gap-1.5">
            <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
            <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
            <span className="h-3 w-3 rounded-full bg-[#28C840]" />
          </div>
          <div className="mx-auto hidden items-center gap-1.5 rounded-md bg-white px-3 py-1 text-[11px] text-zinc-400 ring-1 ring-zinc-200 sm:flex">
            <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
            app.seointent.com/dashboard
          </div>
          <div className="ml-auto h-6 w-6 rounded-full bg-gradient-to-br from-[#FF2F2F] to-[#002FA7] sm:ml-0" />
        </div>

        {/* body */}
        <div className="flex">
          {/* sidebar rail */}
          <aside className="hidden w-14 flex-none flex-col items-center gap-1.5 border-r border-zinc-100 bg-white py-4 sm:flex">
            <div className="mb-2 h-7 w-7 rounded-lg bg-[#FF2F2F]" />
            <RailIcon active d={<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>} />
            <RailIcon d={<><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>} />
            <RailIcon d={<><path d="M3 3v18h18" /><path d="m7 14 4-4 3 3 5-6" /></>} />
            <RailIcon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>} />
            <RailIcon d={<><circle cx="12" cy="12" r="3" /><path d="M12 1v6m0 6v6m11-7h-6m-6 0H1" /></>} />
          </aside>

          {/* main */}
          <div className="min-w-0 flex-1 bg-[#fbfbfc] p-3 sm:p-4">
            {/* toolbar */}
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-[13px] font-bold tracking-tight text-zinc-900">AI Answer Visibility</div>
                <div className="text-[10px] text-zinc-400">Across ChatGPT · Perplexity · Gemini · Claude · Google AIO</div>
              </div>
              <span className="hidden items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-medium text-zinc-500 sm:flex">
                Last 30 days
                <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
              </span>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {/* Big visibility card */}
              <div className="rounded-xl border border-zinc-200/80 bg-white p-4 lg:col-span-2">
                <div className="flex items-end justify-between">
                  <div>
                    <div className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">Total AI citations</div>
                    <div className="mt-0.5 flex items-baseline gap-2">
                      <span className="font-mono-data text-[30px] font-bold leading-none text-zinc-900">3,481</span>
                      <span className="flex items-center gap-0.5 text-[12px] font-bold text-emerald-600">
                        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M7 17 17 7M9 7h8v8" /></svg>
                        18.2%
                      </span>
                    </div>
                  </div>
                  <Sparkline points={[8, 10, 9, 13, 15, 14, 19, 22, 26, 31]} w={130} h={40} />
                </div>

                <div className="mt-3 space-y-1.5">
                  {ENGINES.map((e) => (
                    <div key={e.name} className="flex items-center gap-3 rounded-lg px-1.5 py-1.5 hover:bg-zinc-50">
                      <EngineMark bg={e.bg} glyph={e.glyph} />
                      <span className="w-20 flex-none text-[12px] font-semibold text-zinc-700">{e.name}</span>
                      <Sparkline points={e.pts} stroke={e.bg} w={70} h={20} />
                      <span className="ml-auto font-mono-data text-[12px] font-bold text-zinc-900">{e.count}</span>
                      <span className="w-11 flex-none text-right text-[11px] font-bold text-emerald-600">{e.delta}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* right column */}
              <div className="flex flex-col gap-3">
                {/* cluster map */}
                <div className="rounded-xl border border-zinc-200/80 bg-white p-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-[11px] font-bold text-zinc-900">Keyword clusters</span>
                    <span className="rounded-full bg-[#FF2F2F]/10 px-2 py-0.5 text-[9px] font-bold text-[#FF2F2F]">45 topics</span>
                  </div>
                  <ClusterMap />
                </div>

                {/* content score */}
                <div className="flex items-center gap-3 rounded-xl border border-zinc-200/80 bg-white p-3">
                  <ScoreRing value={94} />
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold text-zinc-900">RankMath score</div>
                    <div className="text-[10px] leading-tight text-zinc-400">27 articles published this month</div>
                    <div className="mt-1 flex items-center gap-1 text-[10px] font-semibold text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Auto-optimized
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* rank movement strip */}
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { k: "ai seo tools", pos: "#3", d: "+6" },
                { k: "claude seo tool", pos: "#1", d: "+4" },
                { k: "llm seo tracking", pos: "#2", d: "+9" },
                { k: "geo optimization", pos: "#5", d: "+3" },
              ].map((r) => (
                <span key={r.k} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-[11px]">
                  <span className="font-medium text-zinc-600">{r.k}</span>
                  <span className="font-mono-data font-bold text-zinc-900">{r.pos}</span>
                  <span className="flex items-center gap-0.5 font-bold text-emerald-600">
                    <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3"><path d="m18 15-6-6-6 6" /></svg>{r.d}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* floating accent card — top left */}
      <div className="animate-float-a absolute -left-6 top-8 hidden rounded-xl border border-zinc-200/80 bg-white/90 px-3 py-2 shadow-xl backdrop-blur lg:block">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/15">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#059669" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
          </span>
          <div>
            <div className="text-[11px] font-bold leading-none text-zinc-900">Cited by ChatGPT</div>
            <div className="mt-0.5 text-[9px] text-zinc-400">2 minutes ago</div>
          </div>
        </div>
      </div>

      {/* floating accent card — bottom right */}
      <div className="animate-float-b absolute -right-4 bottom-12 hidden rounded-xl border border-zinc-200/80 bg-white/90 px-3 py-2 shadow-xl backdrop-blur lg:block">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-[#FF2F2F]/12">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke={RED} strokeWidth="2.2"><path d="M13 2 3 14h9l-1 8 10-12h-9z" /></svg>
          </span>
          <div>
            <div className="text-[11px] font-bold leading-none text-zinc-900">Article published</div>
            <div className="mt-0.5 text-[9px] text-zinc-400">WordPress · 1-click</div>
          </div>
        </div>
      </div>
    </div>
  );
}
